import { type QueryClient, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import { serviceNames } from './constants';
import { getClient, getEnvironment, getService, listServices } from './discoveryService.functions';
import type { Client } from './gen/ai/h2o/cloud/discovery/v1/client_pb';
import type { Environment } from './gen/ai/h2o/cloud/discovery/v1/environment_pb';
import type { ListServicesResponse } from './gen/ai/h2o/cloud/discovery/v1/service_api_pb';
import type { Service } from './gen/ai/h2o/cloud/discovery/v1/service_pb';

export const cacheKeys = {
  environment: [`discovery`, `environment`] as const,
  clientOne: (name: string) => [`discovery`, `client`, `one`, name],
  servicesAll: [`discovery`, `services`, `all`] as const,
  serviceOne: (name: string) => [`discovery`, `services`, `one`, name],
};

export const useQueryEnvironment = <TData = Environment>(
  options?: Omit<UseQueryOptions<Environment, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const baseOptions: UseQueryOptions<Environment, unknown, TData> = {
    queryFn: async ({ signal }) => {
      const response = await getEnvironment({}, { signal });
      return response.environment!;
    },
    queryKey: cacheKeys.environment,
    staleTime: 1000 * 60 * 30, // 30 minutes
  };
  return useQuery({ ...options, ...baseOptions });
};

export const useQueryDiscoverClient = <TData = Client>(
  name: string,
  options?: Omit<UseQueryOptions<Client, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const baseOptions: UseQueryOptions<Client, unknown, TData> = {
    queryFn: async ({ signal }) => {
      const response = await getClient({ name }, { signal });
      return response.client!;
    },
    queryKey: cacheKeys.clientOne(name),
    staleTime: 1000 * 60 * 30, // 30 minutes
  };
  return useQuery({ ...options, ...baseOptions });
};

export const useQueryServices = <TData = ListServicesResponse>(
  options?: Omit<UseQueryOptions<ListServicesResponse, unknown, TData>, `queryFn` | `queryKey`>
) => {
  const queryClient = useQueryClient();
  const baseOptions: UseQueryOptions<ListServicesResponse, unknown, TData> = {
    queryFn: async ({ signal, pageParam }) => {
      // there is typically like 30 services, so even this is a paginated RPC, we are using large pageSize and useQuery
      // to gather everything at once
      const response = await listServices({ pageSize: 1000, pageToken: pageParam }, { signal });
      response.services?.forEach((service) => {
        if (service.name) {
          // prime the QueryCache with all the services for instant retrieval of single service via useQueryService
          queryClient.setQueryData(cacheKeys.serviceOne(service.name), service);
        }
      });
      return response;
    },
    queryKey: cacheKeys.servicesAll,
    staleTime: 1000 * 60 * 30, // 30 minutes
  };
  return useQuery({ ...options, ...baseOptions });
};

export const getServiceQueryOptions = <TData = Service>(
  serviceName: string,
  options?: Omit<UseQueryOptions<Service, unknown, TData>, `queryFn` | `queryKey`>,
  queryClient?: QueryClient
) => {
  const baseOptions: UseQueryOptions<Service, unknown, TData> = {
    initialData: !queryClient
      ? undefined
      : () => {
          const servicesResponse = queryClient.getQueryData<ListServicesResponse>(cacheKeys.servicesAll);
          return servicesResponse?.services?.find((s) => s.name === serviceName);
        },
    queryFn: async ({ signal }) => {
      const response = await getService({ name: serviceName }, { signal });
      return response.service!;
    },
    queryKey: cacheKeys.serviceOne(serviceName),
    staleTime: 1000 * 60 * 30, // 30 minutes
  };
  return { ...options, ...baseOptions };
};

export const useQueryService = <TData = Service>(
  serviceName: string,
  options?: Omit<UseQueryOptions<Service, unknown, TData>, `queryFn` | `queryKey`>
) => {
  return useQuery(getServiceQueryOptions(serviceName, options));
};

export const useQueryMlopsApiUrl = () => {
  return useQueryService<string>(serviceNames.mlops, {
    select: (data) => data.uri!,
  });
};

export const FALLBACK_MLOPS_API_VERSION = '1.0.0';
export const useQueryMlopsApiVersion = () => {
  return useQueryService<string>(serviceNames.mlops, {
    select: (data) => data.version!,
    // Assume we have older api version when data is loading to avoid rendering UI which requires a newer version.
    placeholderData: {
      name: 'services/mlops-api',
      displayName: 'MLOps API',
      uri: '',
      version: FALLBACK_MLOPS_API_VERSION,
    },
  });
};
